/**
 * 命令级结果缓存（跨进程持久化）。
 *
 * 背景：AI 在生成报表时常对同一探查命令发多次（如重复 `schema-ctx --tables A,B`、
 * 重复 `report datasets`、`buildFields` 同一条 SQL）。每次都是独立 Node 进程、
 * 独立网络请求，被预算熔断按相同 token 数重复计费。本模块在 *探查端点入口*
 * 包一层「命令指纹 → 结果」缓存，命中时跳过网络、直接返回（仍走 --out 落盘，
 * 绝不 print 到 stdout，保持既有红线）。
 *
 * 落盘位置：仓库根目录 `.baskreport-cache/command-cache.json`（见 .gitignore）。
 * 跨进程命中：AI 前后两次 shell 命令参数完全一致时，第二次直接读文件缓存。
 */

import { existsSync, mkdirSync, readFileSync, writeFileSync, renameSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { homedir } from 'node:os';
import { createHash } from 'node:crypto';

export interface CacheEntry {
  /** 命令标识，如 buildFields / buildDatabaseTables / buildSchema / listFields / loadBeanFields / loadBeanDatasets / loadFunctions。 */
  command: string;
  /** 已规范化的参数指纹（稳定序列化后的字符串）。 */
  params: string;
  /** 缓存的结果（任意可 JSON 序列化结构）。 */
  result: unknown;
  /** 写入时间戳（ms）。 */
  ts: number;
}

const DEFAULT_TTL_MS = 30 * 60 * 1000; // 30 分钟
const MAX_ENTRIES = 1000;

/** 缓存文件绝对路径（仓库根目录下的 .baskreport-cache/）。 */
export const CACHE_FILE = join(process.cwd(), '.baskreport-cache', 'command-cache.json');

/**
 * 稳定序列化：对键排序、对数组保持顺序（调用方应先规范化，如排序表名列表），
 * 保证相同语义的参数产生相同字符串，跨进程命中。
 */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return '[' + value.map((v) => stableStringify(v)).join(',') + ']';
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return '{' + keys.map((k) => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',') + '}';
}

/** 计算命令指纹（缓存 key）。 */
export function commandKey(command: string, params: unknown): string {
  return command + ':' + stableStringify(params);
}

function readAll(): Record<string, CacheEntry> {
  try {
    if (!existsSync(CACHE_FILE)) return {};
    const raw = readFileSync(CACHE_FILE, 'utf8');
    if (!raw.trim()) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(map: Record<string, CacheEntry>): void {
  try {
    const dir = dirname(CACHE_FILE);
    if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
    const tmp = CACHE_FILE + '.tmp';
    writeFileSync(tmp, JSON.stringify(map), 'utf8');
    renameSync(tmp, CACHE_FILE); // 原子替换，避免半写
  } catch {
    // 缓存失败不应影响主流程
  }
}

/**
 * 取缓存。命中且未过期返回结果，否则返回 undefined。
 * @param ttlMs 可选自定义 TTL（毫秒），默认 30 分钟。
 */
export function getCachedCommand(
  command: string,
  params: unknown,
  ttlMs: number = DEFAULT_TTL_MS,
): unknown | undefined {
  const key = commandKey(command, params);
  const map = readAll();
  const entry = map[key];
  if (!entry) return undefined;
  if (Date.now() - entry.ts > ttlMs) return undefined;
  return entry.result;
}

/**
 * 写缓存。超出上限时按写入时间淘汰最旧条目（LRU 近似）。
 */
export function setCachedCommand(
  command: string,
  params: unknown,
  result: unknown,
  ttlMs: number = DEFAULT_TTL_MS,
  maxEntries: number = MAX_ENTRIES,
): void {
  void ttlMs; // TTL 在读取时判断，此处仅记录 ts
  const key = commandKey(command, params);
  const map = readAll();
  map[key] = { command, params: stableStringify(params), result, ts: Date.now() };
  const keys = Object.keys(map);
  if (keys.length > maxEntries) {
    keys
      .sort((a, b) => ((map[a]?.ts ?? 0) - (map[b]?.ts ?? 0)))
      .slice(0, keys.length - maxEntries)
      .forEach((k) => delete map[k]);
  }
  writeAll(map);
}

/**
 * 规范化探查参数：对字符串/数组去空格、排序（提升跨命令命中率），
 * 其余透传。在调用方构造 params 时使用。
 */
export function normalizeParams(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) {
    if (typeof v === 'string') {
      out[k] = v.trim();
    } else if (Array.isArray(v)) {
      out[k] = v
        .map((x) => (typeof x === 'string' ? x.trim() : x))
        .sort((a, b) => String(a).localeCompare(String(b)));
    } else {
      out[k] = v;
    }
  }
  return out;
}
